import { httpRequest } from '@/shared/api/client';

/** Warning non bloccante restituito dal BE su risposte 2xx (pattern D-X1: `meta.warnings`).
 *  Shape: `{ code, message?, params? }`. Il FE lo rende come banner/toast informativo. */
export interface WorkflowWarning {
  code: string;
  message?: string;
  params?: Record<string, unknown>;
}

export interface WorkflowResult<T = Record<string, unknown>> {
  id: string;
  /** Nuovo stato dopo la transizione (da `meta.transitionedTo`). */
  transitionedTo?: string;
  /** Attributi PARZIALI dall'envelope custom: NON usarli come fonte completa,
   *  rifare una fetchOne per il documento intero. */
  attributes: T;
  warnings: WorkflowWarning[];
  /**
   * `meta` completo di primo livello. Alcuni endpoint ci mettono contatori d'esito
   * oltre a `transitionedTo`/`warnings` — es. `cancel-days` restituisce
   * `cancelledDaysCount`, `remainingActiveDaysCount`, `newDurationValue`.
   */
  meta: Record<string, unknown>;
}

/**
 * Esegue un'azione di workflow (state-machine) su una risorsa:
 *   `POST /api/{type}/{id}/{action}` con body JSON semplice (es. `{ note }`, `{ rejectionReason }`).
 *
 * I controller workflow del pacchetto HR rispondono con un **envelope custom** non JSON:API:
 *   `{ data: { id, attributes }, meta: { transitionedTo, warnings? } }`
 * → questa funzione lo normalizza; il chiamante deve poi rifare `fetchOne` per il record completo.
 * Gli errori seguono la shape standard `{ errors: [{ code, detail, meta:{ code } }] }` gestita
 * dal parser esistente (`getApiCode`/`getUserMessage`).
 */
export async function runWorkflowAction<T = Record<string, unknown>>(
  type: string,
  id: string,
  action: string,
  body?: Record<string, unknown>,
): Promise<WorkflowResult<T>> {
  const doc = await httpRequest<{
    data?: { id?: string; attributes?: T };
    meta?: { transitionedTo?: string; warnings?: WorkflowWarning[] } & Record<string, unknown>;
  }>(`/api/${type}/${encodeURIComponent(id)}/${action}`, {
    method: 'POST',
    body: body ?? {},
    endpointType: 'plain',
  });
  return {
    id: doc?.data?.id ?? id,
    transitionedTo: doc?.meta?.transitionedTo,
    attributes: (doc?.data?.attributes ?? {}) as T,
    warnings: Array.isArray(doc?.meta?.warnings) ? doc!.meta!.warnings! : [],
    meta: (doc?.meta ?? {}) as Record<string, unknown>,
  };
}
